import React, { Fragment, useEffect, useState } from "react"
import { graphql } from "gatsby"
import { injectIntl, Link } from "gatsby-plugin-intl"
import styled from "@emotion/styled"

import TheyLoveUsBanner from "../../components/TheyLoveUsBanner"
import ClientSlider from "../../components/ClientSlider"
import TvAdsSection from "../../components/Layout/TvAdsSection"
import ProductVideoSection from "../../components/Products/ProductVideoSection"
import ProductExpert from "../../components/ProductExpert"
import Testimonials from "../../components/Layout/Testimonials"
import Footer from "../../components/Layout/Footer"
import SeoComp from "../../components/SeoComp"
import { getCount } from "../../utils/externalApiHelpers"
import BusinessSlider from "../../components/BusinessSlider"
import TrustPilotWidgetSlider from "../../components/Layout/TrustPilotWidgetSlider"
import { pageViewEventScript } from "../../utils/additionalScriptHelpers"

const Section = styled.div`
  color: #262626;
  background-color: ${({ color }) => (color ? color : "#fff")};
  position: relative;
  font-family: "GothamLight";
  width: 100%;
  font-weight: normal;
  padding: 30px 20px;
`

const Container = styled.div`
  color: #262626;
  position: relative;
  font-family: "GothamLight";
  width: 100%;
  max-width: 1080px;
  margin: 0px auto;
  font-weight: normal;
`

const Title = styled.h2`
  color: #262626;
  text-align: center;
  position: relative;
  width: 100%;
  text-transform: uppercase;
  font-size: 36px;
  font-family: "BebasNeueBold";
  font-weight: bold;
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;

  :after {
    content: "";
    height: 3px;
    background: #262626;
    width: 120px;
    margin: 0 auto;
    margin-top: 5px;
  }

  @media (max-width: 550px) {
    font-size: 24px;
    text-align: left;

    :after {
      margin: 5px 0px;
    }
  }
`

const Subtitle = styled.h5`
  color: #262626;
  text-align: center;
  position: relative;
  font-family: "GothamLight";
  font-size: 16px;
  line-height: 1.4em;
  width: 100%;
  margin-bottom: 20px;
  font-weight: bold;

  @media (max-width: 550px) {
    text-align: left;
  }
`

const Text = styled.h3`
  color: #262626;
  text-align: center;
  position: relative;
  font-family: "GothamLight";
  font-size: 21px;
  line-height: 1.4em;
  width: 100%;
  text-transform: uppercase;
  font-weight: bold;
  margin: 0px;

  @media (max-width: 550px) {
    font-size: 17px;
    text-align: ${({ align }) => (align ? align : "center")};
    margin-bottom: ${({ marginBottom }) =>
      marginBottom ? marginBottom : "0px"};
  }

  > strong {
    font-family: "Gotham Book";
  }
`

const DesktopText = styled.h3`
  color: #262626;
  text-align: center;
  position: relative;
  font-family: "GothamLight";
  font-size: 21px;
  line-height: 1.4em;
  width: 100%;
  text-transform: uppercase;
  font-weight: bold;

  @media (max-width: 550px) {
    font-size: 18px;
    display: none;
  }

  > strong {
    font-family: "Gotham Book";
  }
`

const MobileText = styled.div`
  color: #262626;
  text-align: center;
  position: relative;
  font-family: "GothamLight";
  font-size: 21px;
  line-height: 1.4em;
  width: 100%;
  text-transform: uppercase;
  font-weight: bold;
  display: none;

  @media (max-width: 550px) {
    font-size: 18px;
    display: block;
  }

  > strong {
    font-family: "Gotham Book";
  }
`

const LeftText = styled.div`
  color: #262626;
  text-align: center;
  position: relative;
  font-family: "GothamLight";
  font-size: 21px;
  line-height: 1.4em;
  width: 100%;
  text-transform: uppercase;
  font-weight: bold;
  display: none;

  @media (max-width: 550px) {
    font-size: 18px;
    display: block;
    text-align: left;
  }

  > strong {
    font-family: "Gotham Book";
  }
`

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  max-width: 655px;
  margin: 0px auto;
  margin-top: 15px;
  position: relative;
  z-index: 1;

  @media (max-width: 550px) {
    flex-direction: column;
  }
`

const DesktopButton = styled(Link)`
  background: ${props => props.theme.colors.button.black};
  color: #fff;
  border-radius: 5px;
  font-size: 18px;
  line-height: 1;
  max-width: 100%;
  font-family: "Museo";
  font-weight: 700;
  text-align: center;
  padding: 12px 20px;
  position: relative;

  :hover {
    color: #262626;
    background: ${props => props.theme.gradient.buttonGradient1};
  }

  @media (max-width: 550px) {
    width: 100%;
    font-size: 16px;
  }
`

const MobileButton = styled(Link)`
  color: #fff;
  text-align: center;
  position: relative;
  width: 100%;
  padding: 10px 15px;
  border-radius: 6px;
  background: #262626;
  margin: 0px 15px;
  text-transform: uppercase;
  font-weight: bold;
  max-width: 300px;
  display: none;

  @media (max-width: 550px) {
    max-width: 100%;
    margin: 10px 0px;
    display: block;
  }

  &:hover {
    color: #fff;
  }
`

const ExpertImageSection = styled.div`
  display: flex;
  flex-direction: row;
  margin: 20px 0px;

  > div {
    flex: 1;
    padding: 0px 15px;
  }

  > div:nth-of-type(2) {
    background-image: url(https://static.percko.com/uploads/889dc717-d9d8-4329-916b-11e97dc19e17.png);
    background-repeat: no-repeat;
    background-position: 95% 80%;
    background-size: 80px;
  }

  @media (max-width: 550px) {
    flex-direction: column;

    > div {
      padding: 0px;
    }

    > div:nth-of-type(2) {
      background-position: 95% 100%;
    }
  }
`

const ExpertImage = styled.img`
  width: 100%;
  border-radius: 10px;
  margin: 0px;
  object-fit: contain;

  @media (max-width: 550px) {
    margin-bottom: 15px;
  }
`

const ExpertTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
  font-family: "GothamLight";

  @media (max-width: 550px) {
    font-size: 16px;
    margin: 10px 0px;
  }
`

const PageSection = styled.div`
  width: 100%;
  padding: 30px 20px;
  background-color: ${({ background }) => (background ? background : "#fff")};
`

const ExpertText = styled.div`
  font-size: 16px;
  line-height: 1.4em;
`

const ExpertList = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px 0px;
`

const DesktopSection = styled.div`
  display: flex;

  @media (max-width: 550px) {
    display: none;
  }
`

const MobileSection = styled.div`
  display: none;

  @media (max-width: 550px) {
    display: flex;
  }
`

const StarSection = styled.div`
  position: absolute;
  right: 0px;
  top: 30px;
  text-align: right;
  display: none;

  @media (max-width: 850px) {
    text-align: center;
    position: relative;
    left: 0px;
    right: 0px;
    top: 0px;
    margin: 0px auto 15px;
  }

  @media (max-width: 550px) {
    text-align: left;
  }
`

const StarRating = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 850px) {
    justify-content: center;
  }

  @media (max-width: 550px) {
    justify-content: flex-start;
  }
`

const StarImage = styled.img`
  max-width: 95px;
  width: 100%;
  margin: 0px 0px 0px 7px;
`

const StarSource = styled.div`
  font-style: italic;
`

const TrustPilotSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 0 auto 15px auto;

  iframe {
    margin-bottom: 5px;
    margin-top: 5px;
  }
  @media (max-width: 550px) {
    align-items: flex-start;
    width: 100%;
  }
`

const DesktopTrustPilotSection = styled.div`
  width: 100%;

  @media (max-width: 550px) {
    display: none;
  }
`

const MobileTrustPilotSection = styled.div`
  width: 100%;
  display: none;

  @media (max-width: 550px) {
    display: block;
  }
`

const TheyLoveUs = ({ data, intl }) => {
  const [count, setcount] = useState({
    en: "130 000",
    fr: "130 000",
    de: "130 000",
    es: "130 000",
  })

  const counter = async () => {
    let tmpCount = { ...count }
    let count = await getCount()
    if (count.status === "success") {
      tmpCount["fr"] = new Intl.NumberFormat("fr-FR", {
        style: "decimal",
      }).format(count.data)
      tmpCount["en"] = new Intl.NumberFormat("en-EU", {
        style: "decimal",
      }).format(count.data)
      tmpCount["de"] = new Intl.NumberFormat("de-DE", {
        style: "decimal",
      }).format(count.data)
      tmpCount["es"] = new Intl.NumberFormat("es-ES", {
        style: "decimal",
      }).format(count.data)
    } else {
      tmpCount["fr"] = new Intl.NumberFormat("fr-FR", {
        style: "decimal",
      }).format(130000)
      tmpCount["en"] = new Intl.NumberFormat("en-EU", {
        style: "decimal",
      }).format(130000)
      tmpCount["de"] = new Intl.NumberFormat("de-DE", {
        style: "decimal",
      }).format(130000)
      tmpCount["es"] = new Intl.NumberFormat("es-ES", {
        style: "decimal",
      }).format(130000)
    }
    setcount({ ...tmpCount })
  }

  useEffect(() => {
    counter()

    data.allDirectusSeoTranslation.nodes[0] &&
      data.allDirectusSeoTranslation.nodes[0].meta_title &&
      pageViewEventScript(
        data.allDirectusSeoTranslation.nodes[0].meta_title,
        intl.locale
      )
  }, [])
  return (
    data &&
    data.allDirectusTheyLoveUsTranslation.nodes[0] && (
      <Fragment>
        <SeoComp
          description={
            data.allDirectusSeoTranslation.nodes[0] &&
            data.allDirectusSeoTranslation.nodes[0].meta_description
          }
          title={
            data.allDirectusSeoTranslation.nodes[0] &&
            data.allDirectusSeoTranslation.nodes[0].meta_title
          }
          twitter_card={
            data.allDirectusSeoTranslation.nodes[0] &&
            data.allDirectusSeoTranslation.nodes[0].twitter_card
          }
          image={
            data.allDirectusSeoTranslation.nodes[0] &&
            data.allDirectusSeoTranslation.nodes[0].image &&
            data.allDirectusSeoTranslation.nodes[0].image.data &&
            data.allDirectusSeoTranslation.nodes[0].image.data.url
          }
          hreflangPathname={{
            fr: "ils-nous-aiment",
            en: "they-love-us",
            de: "they-love-us",
            es: "they-love-us",
          }}
        />
        {data.allDirectusTheyLoveUsTranslation.nodes[0].top_banner_section && (
          <TheyLoveUsBanner
            data={
              data.allDirectusTheyLoveUsTranslation.nodes[0].top_banner_section
            }
          />
        )}

        {process.env.GATSBY_ACTIVE_ENV === "gbp" && (
          <Section>
            <Container>
              {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .expert_section_title && (
                <Title
                  dangerouslySetInnerHTML={{
                    __html:
                      data.allDirectusTheyLoveUsTranslation.nodes[0]
                        .expert_section_title,
                  }}
                />
              )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .expert_section_subtitle && (
                <Subtitle
                  dangerouslySetInnerHTML={{
                    __html:
                      data.allDirectusTheyLoveUsTranslation.nodes[0]
                        .expert_section_subtitle,
                  }}
                />
              )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .expert_image_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0]
                  .expert_image_section.image && (
                  <ExpertImageSection>
                    <div>
                      <ExpertImage
                        className="lazyload"
                        data-src={
                          data.allDirectusTheyLoveUsTranslation.nodes[0]
                            .expert_image_section.image
                        }
                      />
                    </div>
                    <div>
                      <ExpertTitle
                        dangerouslySetInnerHTML={{
                          __html:
                            data.allDirectusTheyLoveUsTranslation.nodes[0]
                              .expert_image_section.title,
                        }}
                      />
                      <ExpertText
                        dangerouslySetInnerHTML={{
                          __html:
                            data.allDirectusTheyLoveUsTranslation.nodes[0]
                              .expert_image_section.text,
                        }}
                      />
                    </div>
                  </ExpertImageSection>
                )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section
                  .title && (
                  <DesktopText
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .expert_section.title,
                    }}
                  />
                )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section
                  .title && (
                  <LeftText
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .expert_section.title,
                    }}
                  />
                )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section
                  .list.length > 0 && (
                  <ExpertList>
                    <DesktopSection>
                      <ProductExpert
                        data={
                          data.allDirectusTheyLoveUsTranslation.nodes[0]
                            .expert_section.list
                        }
                      />
                    </DesktopSection>
                    <MobileSection>
                      <ProductExpert
                        singleQuote="single"
                        data={
                          data.allDirectusTheyLoveUsTranslation.nodes[0]
                            .expert_section.list
                        }
                      />
                    </MobileSection>
                  </ExpertList>
                )}
              {/* {data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section
                  .bottom_text && (
                  <DesktopText
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .expert_section.bottom_text,
                    }}
                  />
                )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section
                  .mobile_bottom_text && (
                  <MobileText
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .expert_section.mobile_bottom_text,
                    }}
                  />
                )} */}

              <ButtonWrapper>
                {data.allDirectusTheyLoveUsTranslation.nodes[0].button_text && (
                  <DesktopButton
                    to={
                      process.env.GATSBY_CURRENCY_TYPE === "eur" &&
                      intl.locale === "fr"
                        ? "/shop/solutions-textiles/tshirt-discret/?Gender=Female&Collar=V&Color=Noir&Size=&Offer=Seul"
                        : "/shop/lyneup/"
                    }
                  >
                    {data.allDirectusTheyLoveUsTranslation.nodes[0].button_text}
                  </DesktopButton>
                )}
              </ButtonWrapper>
            </Container>
          </Section>
        )}

        {process.env.GATSBY_ACTIVE_ENV === "gbp" && (
          <Section color="#f2f2f2">
            <Container>
              <div>
                {data.allDirectusTheyLoveUsTranslation.nodes[0]
                  .client_testimonials &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .client_testimonials.title && (
                    <Title
                      dangerouslySetInnerHTML={{
                        __html:
                          data.allDirectusTheyLoveUsTranslation.nodes[0]
                            .client_testimonials.title,
                      }}
                    />
                  )}
                {data.allDirectusTheyLoveUsTranslation.nodes[0]
                  .client_testimonials &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .client_testimonials.subtitle && (
                    <Subtitle
                      dangerouslySetInnerHTML={{
                        __html: data.allDirectusTheyLoveUsTranslation.nodes[0].client_testimonials.subtitle.replace(
                          "130 000",
                          count[intl.locale]
                        ),
                      }}
                    />
                  )}
                {data.allDirectusTheyLoveUsTranslation.nodes[0].star_rating && (
                  <StarSection>
                    <StarRating>
                      {
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .star_rating
                      }
                      {data.allDirectusTheyLoveUsTranslation.nodes[0]
                        .star_rating_image && (
                        <StarImage
                          className="lazyload"
                          data-src={
                            data.allDirectusTheyLoveUsTranslation.nodes[0]
                              .star_rating_image
                          }
                        />
                      )}
                    </StarRating>
                    {data.allDirectusTheyLoveUsTranslation.nodes[0]
                      .star_rating_text && (
                      <StarSource>
                        {
                          data.allDirectusTheyLoveUsTranslation.nodes[0]
                            .star_rating_text
                        }
                      </StarSource>
                    )}
                  </StarSection>
                )}

                {(intl.locale === "fr" || intl.locale === "en") && (
                  <TrustPilotWidgetSlider intl={intl} tags="HP" />
                )}
                {(intl.locale === "de" || intl.locale === "es") &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .client_testimonials &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .client_testimonials.list && (
                    <ClientSlider
                      data={
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .client_testimonials.list
                      }
                    />
                  )}
              </div>

              {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .video_testimonials &&
                data.allDirectusTheyLoveUsTranslation.nodes[0]
                  .video_testimonials.title && (
                  <Text
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .video_testimonials.title,
                    }}
                    align="left"
                    marginBottom="10px"
                  />
                )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .video_testimonials && (
                <ProductVideoSection
                  data={
                    data.allDirectusTheyLoveUsTranslation.nodes[0]
                      .video_testimonials
                  }
                  page="ils-nous-aiment"
                />
              )}

              {/* {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .video_testimonials &&
                data.allDirectusTheyLoveUsTranslation.nodes[0]
                  .video_testimonials.bottom_text && (
                  <Text
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .video_testimonials.bottom_text,
                    }}
                  />
                )} */}

              <ButtonWrapper>
                {data.allDirectusTheyLoveUsTranslation.nodes[0]
                  .video_testimonials &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .button_text && (
                    <DesktopButton
                      to={
                        process.env.GATSBY_CURRENCY_TYPE === "eur" &&
                        intl.locale === "fr"
                          ? "/shop/solutions-textiles/tshirt-discret/?Gender=Female&Collar=V&Color=Noir&Size=&Offer=Seul"
                          : "/shop/lyneup/"
                      }
                    >
                      {
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .button_text
                      }
                    </DesktopButton>
                  )}
              </ButtonWrapper>
            </Container>
          </Section>
        )}

        <PageSection>
          <Container>
            {data.allDirectusTheyLoveUsTranslation.nodes[0]
              .business_expert_title && (
              <Title
                dangerouslySetInnerHTML={{
                  __html:
                    data.allDirectusTheyLoveUsTranslation.nodes[0]
                      .business_expert_title,
                }}
                id="entreprise"
              />
            )}
            {data.allDirectusTheyLoveUsTranslation.nodes[0]
              .business_expert_slider && (
              <BusinessSlider
                data={
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .business_expert_slider
                }
              />
            )}
          </Container>
        </PageSection>

        <Section>
          {data.allDirectusTvAdsSectionTranslation.nodes.length > 0 && (
            <TvAdsSection
              color="#fff"
              data={data.allDirectusTvAdsSectionTranslation.nodes[0]}
              page="LoveUs"
            />
          )}

          <ButtonWrapper>
            {data.allDirectusTheyLoveUsTranslation.nodes[0].button_text && (
              <DesktopButton to="/shop/">
                {intl.locale === "fr"
                  ? "Je découvre les produits PERCKO"
                  : data.allDirectusTheyLoveUsTranslation.nodes[0].button_text}
              </DesktopButton>
            )}
          </ButtonWrapper>
        </Section>

        {process.env.GATSBY_ACTIVE_ENV != "gbp" && (
          <Section color="#f2f2f2">
            <Container>
              <div>
                {process.env.GATSBY_ACTIVE_ENV != "eur" &&
                  intl.locale != "fr" &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .client_testimonials &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .client_testimonials.title && (
                    <Title
                      dangerouslySetInnerHTML={{
                        __html:
                          data.allDirectusTheyLoveUsTranslation.nodes[0]
                            .client_testimonials.title,
                      }}
                    />
                  )}
                {process.env.GATSBY_ACTIVE_ENV != "eur" &&
                  intl.locale != "fr" &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .client_testimonials &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .client_testimonials.subtitle && (
                    <Subtitle
                      dangerouslySetInnerHTML={{
                        __html: data.allDirectusTheyLoveUsTranslation.nodes[0].client_testimonials.subtitle.replace(
                          "130 000",
                          count[intl.locale]
                        ),
                      }}
                    />
                  )}
                {data.allDirectusTheyLoveUsTranslation.nodes[0].star_rating && (
                  <StarSection>
                    <StarRating>
                      {
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .star_rating
                      }
                      {data.allDirectusTheyLoveUsTranslation.nodes[0]
                        .star_rating_image && (
                        <StarImage
                          className="lazyload"
                          data-src={
                            data.allDirectusTheyLoveUsTranslation.nodes[0]
                              .star_rating_image
                          }
                        />
                      )}
                    </StarRating>
                    {data.allDirectusTheyLoveUsTranslation.nodes[0]
                      .star_rating_text && (
                      <StarSource>
                        {
                          data.allDirectusTheyLoveUsTranslation.nodes[0]
                            .star_rating_text
                        }
                      </StarSource>
                    )}
                  </StarSection>
                )}

                {(intl.locale === "fr" || intl.locale === "en") && (
                  <TrustPilotWidgetSlider intl={intl} tags="HP" />
                )}
                {(intl.locale === "de" || intl.locale === "es") &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .client_testimonials &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .client_testimonials.list && (
                    <ClientSlider
                      data={
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .client_testimonials.list
                      }
                    />
                  )}
              </div>

              {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .video_testimonials &&
                data.allDirectusTheyLoveUsTranslation.nodes[0]
                  .video_testimonials.title && (
                  <Text
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .video_testimonials.title,
                    }}
                    align="left"
                    marginBottom="10px"
                  />
                )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .video_testimonials && (
                <ProductVideoSection
                  data={
                    data.allDirectusTheyLoveUsTranslation.nodes[0]
                      .video_testimonials
                  }
                  page="ils-nous-aiment"
                />
              )}

              {/* {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .video_testimonials &&
                data.allDirectusTheyLoveUsTranslation.nodes[0]
                  .video_testimonials.bottom_text && (
                  <Text
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .video_testimonials.bottom_text,
                    }}
                  />
                )} */}

              <ButtonWrapper>
                {data.allDirectusTheyLoveUsTranslation.nodes[0]
                  .video_testimonials &&
                  data.allDirectusTheyLoveUsTranslation.nodes[0]
                    .button_text && (
                    <DesktopButton
                      to={
                        process.env.GATSBY_CURRENCY_TYPE === "eur" &&
                        intl.locale === "fr"
                          ? "/shop/solutions-textiles/tshirt-discret/?Gender=Female&Collar=V&Color=Noir&Size=&Offer=Seul"
                          : "/shop/lyneup/"
                      }
                    >
                      {
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .button_text
                      }
                    </DesktopButton>
                  )}
              </ButtonWrapper>
            </Container>
          </Section>
        )}

        {process.env.GATSBY_ACTIVE_ENV != "gbp" && (
          <Section>
            <Container>
              {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .expert_section_title && (
                <Title
                  dangerouslySetInnerHTML={{
                    __html:
                      data.allDirectusTheyLoveUsTranslation.nodes[0]
                        .expert_section_title,
                  }}
                />
              )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .expert_section_subtitle && (
                <Subtitle
                  dangerouslySetInnerHTML={{
                    __html:
                      data.allDirectusTheyLoveUsTranslation.nodes[0]
                        .expert_section_subtitle,
                  }}
                />
              )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0]
                .expert_image_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0]
                  .expert_image_section.image && (
                  <ExpertImageSection>
                    <div>
                      <ExpertImage
                        className="lazyload"
                        data-src={
                          data.allDirectusTheyLoveUsTranslation.nodes[0]
                            .expert_image_section.image
                        }
                      />
                    </div>
                    <div>
                      <ExpertTitle
                        dangerouslySetInnerHTML={{
                          __html:
                            data.allDirectusTheyLoveUsTranslation.nodes[0]
                              .expert_image_section.title,
                        }}
                      />
                      <ExpertText
                        dangerouslySetInnerHTML={{
                          __html:
                            data.allDirectusTheyLoveUsTranslation.nodes[0]
                              .expert_image_section.text,
                        }}
                      />
                    </div>
                  </ExpertImageSection>
                )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section
                  .title && (
                  <DesktopText
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .expert_section.title,
                    }}
                  />
                )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section
                  .title && (
                  <LeftText
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .expert_section.title,
                    }}
                  />
                )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section
                  .list.length > 0 && (
                  <ExpertList>
                    <DesktopSection>
                      <ProductExpert
                        data={
                          data.allDirectusTheyLoveUsTranslation.nodes[0]
                            .expert_section.list
                        }
                      />
                    </DesktopSection>
                    <MobileSection>
                      <ProductExpert
                        singleQuote="single"
                        data={
                          data.allDirectusTheyLoveUsTranslation.nodes[0]
                            .expert_section.list
                        }
                      />
                    </MobileSection>
                  </ExpertList>
                )}
              {/* {data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section
                  .bottom_text && (
                  <DesktopText
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .expert_section.bottom_text,
                    }}
                  />
                )}
              {data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section &&
                data.allDirectusTheyLoveUsTranslation.nodes[0].expert_section
                  .mobile_bottom_text && (
                  <MobileText
                    dangerouslySetInnerHTML={{
                      __html:
                        data.allDirectusTheyLoveUsTranslation.nodes[0]
                          .expert_section.mobile_bottom_text,
                    }}
                  />
                )} */}

              <ButtonWrapper>
                {data.allDirectusTheyLoveUsTranslation.nodes[0].button_text && (
                  <DesktopButton
                    to={
                      process.env.GATSBY_CURRENCY_TYPE === "eur" &&
                      intl.locale === "fr"
                        ? "/shop/solutions-textiles/tshirt-discret/?Gender=Female&Collar=V&Color=Noir&Size=&Offer=Seul"
                        : "/shop/lyneup/"
                    }
                  >
                    {data.allDirectusTheyLoveUsTranslation.nodes[0].button_text}
                  </DesktopButton>
                )}
              </ButtonWrapper>
            </Container>
          </Section>
        )}

        <div>
          {data.allDirectusCommonTranslation.nodes[0].testimonials && (
            <Testimonials
              data={
                data.allDirectusCommonTranslation.nodes[0].testimonials.list
              }
            />
          )}
        </div>

        {data.allDirectusFooterTranslation.nodes.length > 0 && (
          <Footer data={data.allDirectusFooterTranslation.nodes[0]} />
        )}
      </Fragment>
    )
  )
}

export const query = graphql`
  query($lang: String!) {
    allDirectusTheyLoveUsTranslation(filter: { language: { eq: $lang } }) {
      nodes {
        top_banner_section {
          desktop_image
          mobile_image
          title
          subtitle
        }
        business_expert_title
        business_expert_slider {
          list {
            image
            text
            button_text
            button_url
          }
        }
        button_text
        men_button_text
        women_button_text
        star_rating
        star_rating_image
        star_rating_text
        client_testimonials {
          title
          subtitle
          list {
            image
            name
            text
          }
        }
        video_testimonials {
          title
          bottom_text
          button_text
          testimonials {
            title
            author
            thumb
            video
          }
        }
        expert_section_title
        expert_section_subtitle
        expert_image_section {
          image
          title
          text
        }
        expert_section {
          title
          mobile_title
          bottom_text
          mobile_bottom_text
          list {
            image
            name
            designation
            text
            video_text
            video_url
          }
        }
      }
    }
    allDirectusTvAdsSectionTranslation(filter: { language: { eq: $lang } }) {
      nodes {
        title
        video_title
        love_us_video_title
        video_text
        love_us_video_text
        video_url
        slider_title
        love_us_slider_title
        slider_text
        love_us_slider_text
        page_link_text
        slider {
          list {
            image
            text
            link
          }
        }
        slider_link_text
      }
    }
    allDirectusFooterTranslation(filter: { language: { eq: $lang } }) {
      nodes {
        facebook
        gbp_facebook
        youtube
        twitter
        instagram
        linkedin
        about_us {
          name
          list {
            link
            list
          }
        }
        contact {
          name
          list {
            link
            list
          }
          button {
            link
            list
          }
        }
        purchases {
          list {
            list
            link
          }
          name
        }
        help {
          list {
            link
            list
          }
          name
        }
        about_us_menu {
          name
          list {
            link
            list
          }
        }
        knowledge_center_menu {
          name
          list {
            link
            list
          }
        }
        shop_menu {
          name
          list {
            link
            list
          }
        }
        support_menu {
          name
          list {
            link
            list
          }
          button {
            link
            list
          }
        }
        newsletter_heading
        follow_us_heading
        footer_bottom {
          list {
            link
            list
          }
        }
      }
    }
    allDirectusCommonTranslation(filter: { language: { eq: $lang } }) {
      nodes {
        testimonials {
          list {
            image
            text
          }
        }
      }
    }
    allDirectusSeoTranslation(
      filter: {
        language: { eq: $lang }
        seo: { page_name: { eq: "ils_nous_aiment" } }
      }
    ) {
      nodes {
        meta_title
        meta_description
        twitter_card
        twitter_title
        url
        image {
          data {
            url
          }
        }
      }
    }
  }
`

export default injectIntl(TheyLoveUs)
